import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { reportProductNotFound } from "../service/monitoring";

export function normalizeProductType(value) {
  if (typeof value !== "string") return "";
  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch (_) {
    // malformed %-escape: fall back to the raw value
  }
  return decoded.toLowerCase().trim();
}

/**
 * Resolves the product matching the `:type` route param against the Redux
 * product list, distinguishing "still loading" from "genuinely not found" so
 * callers never read fields off an undefined product. Reports a not-found
 * once per distinct type value (no repeated/looping Sentry events).
 */
export function useProductResolution(rawType) {
  const { Produits, loading } = useSelector((state) => state.dataApp);
  const normalizedType = useMemo(() => normalizeProductType(rawType), [rawType]);
  const product = useMemo(
    () => Produits.find((p) => normalizeProductType(p?.NomProduit) === normalizedType),
    [Produits, normalizedType]
  );
  const stillResolving = loading || Produits.length === 0;
  const reportedForType = useRef(null);

  useEffect(() => {
    if (product || stillResolving) return;
    if (reportedForType.current === normalizedType) return;
    reportedForType.current = normalizedType;
    reportProductNotFound({
      raw_type: rawType,
      normalized_type: normalizedType,
      pathname: typeof window !== "undefined" ? window.location.pathname : undefined,
      produits_loaded_count: Produits.length,
      produits_loading: loading,
      available_names: Produits.length <= 50 ? Produits.map((p) => p?.NomProduit) : undefined,
    });
  }, [product, stillResolving, normalizedType, rawType, Produits, loading]);

  return {
    product,
    status: stillResolving ? "loading" : product ? "found" : "not_found",
  };
}
